import { useState, useEffect } from 'react';
import MobileSheet from './MobileSheet';
import ConfirmModal from '../ConfirmModal';
import { ChevronLeftIcon, PlusIcon, DeleteIcon, TemplateIcon, DownloadIcon, ChevronRightIcon } from './Icons';
import { TYPE_CONFIG, HIERARCHY_RULES, CONTAINER_TYPES } from '../../constants';
import api from '../../api';
import './mobile.css';

function generateTempId() {
  return crypto.randomUUID();
}

function MobileTemplateNode({ item, allItems, depth, onUpdate, onDelete, onAddChild }) {
  const [expanded, setExpanded] = useState(true);
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
  const Icon = config.icon;
  const children = allItems.filter((i) => i.parentTempId === item.tempId);
  const childTypes = HIERARCHY_RULES[item.type] || [];
  const isContainer = CONTAINER_TYPES.includes(item.type);

  return (
    <>
      <div className="mobile-template-node" style={{ paddingLeft: `${depth * 20 + 8}px` }}>
        {isContainer && children.length > 0 ? (
          <ChevronRightIcon size={14} className={`backlog-history-chevron${expanded ? ' backlog-history-chevron--open' : ''}`} onClick={() => setExpanded((v) => !v)} />
        ) : null}
        <span style={{ color: config.color, flexShrink: 0 }}><Icon size={16} /></span>
        <input
          className="mobile-input mobile-template-title"
          value={item.title}
          onChange={(e) => onUpdate(item.tempId, { title: e.target.value })}
          placeholder="Title"
        />
        {childTypes.length > 0 && (
          <button className="mobile-btn-icon" onClick={() => onAddChild(item.tempId, childTypes[0])}>
            <PlusIcon size={16} />
          </button>
        )}
        <button className="mobile-btn-icon" onClick={() => onDelete(item.tempId)}>
          <DeleteIcon size={16} />
        </button>
      </div>
      {expanded && children
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((child) => (
          <MobileTemplateNode
            key={child.tempId}
            item={child}
            allItems={allItems}
            depth={depth + 1}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onAddChild={onAddChild}
          />
        ))
      }
    </>
  );
}

export default function TemplatesMobile({ onBack, showToast, userProfile, sprintId, onItemsImported, projectType }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importing, setImporting] = useState(null);

  // Editor state
  const [editorName, setEditorName] = useState('');
  const [editorDesc, setEditorDesc] = useState('');
  const [editorItems, setEditorItems] = useState([]);
  const [editorSaving, setEditorSaving] = useState(false);

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await api.getTemplates();
      setTemplates(data);
    } catch (err) {
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (template) => {
    setEditingTemplate(template);
    setEditorName(template?.name || '');
    setEditorDesc(template?.description || '');
    setEditorItems(template?.items || []);
    setShowEditor(true);
  };

  const handleAddRoot = (type) => {
    const maxOrder = editorItems.reduce((max, i) => Math.max(max, i.order || 0), 0);
    setEditorItems((prev) => [...prev, {
      tempId: generateTempId(), parentTempId: null, type, title: '', description: '', storyPoints: null, order: maxOrder + 1,
    }]);
  };

  const handleAddChild = (parentTempId, type) => {
    const siblings = editorItems.filter((i) => i.parentTempId === parentTempId);
    const maxOrder = siblings.reduce((max, i) => Math.max(max, i.order || 0), 0);
    setEditorItems((prev) => [...prev, {
      tempId: generateTempId(), parentTempId, type, title: '', description: '', storyPoints: null, order: maxOrder + 1,
    }]);
  };

  const handleUpdateItem = (tempId, updates) => {
    setEditorItems((prev) => prev.map((i) => i.tempId === tempId ? { ...i, ...updates } : i));
  };

  const handleDeleteItem = (tempId) => {
    const toRemove = new Set();
    const collect = (id) => {
      toRemove.add(id);
      editorItems.filter((i) => i.parentTempId === id).forEach((c) => collect(c.tempId));
    };
    collect(tempId);
    setEditorItems((prev) => prev.filter((i) => !toRemove.has(i.tempId)));
  };

  const handleSaveEditor = async () => {
    if (!editorName.trim()) { showToast('Name required', 'error'); return; }
    setEditorSaving(true);
    try {
      const data = { name: editorName.trim(), description: editorDesc.trim(), templateType: 'scrum', items: editorItems.filter((i) => i.title.trim()) };
      if (editingTemplate?.id) {
        await api.updateTemplate(editingTemplate.id, data);
      } else {
        await api.createTemplate({ ...data, createdBy: userProfile.uid });
      }
      showToast(editingTemplate?.id ? 'Template updated' : 'Template created', 'success');
      setShowEditor(false);
      loadTemplates();
    } catch (err) {
      showToast('Failed to save template', 'error');
    } finally {
      setEditorSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteTemplate(deleteTarget.id);
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      setDeleteTarget(null);
      showToast('Template deleted', 'success');
    } catch (err) {
      showToast('Failed to delete template', 'error');
    }
  };

  const handleImport = async (template) => {
    const tType = template.templateType || 'scrum';
    if (projectType && tType !== projectType) {
      showToast(`This template is for ${tType === 'scrum' ? 'Scrum' : 'Kanban'} projects only`, 'error');
      return;
    }
    setImporting(template.id);
    try {
      const created = await api.importTemplate(template.id, sprintId, projectType);
      showToast(`Imported ${created.length} items`, 'success');
      if (onItemsImported) onItemsImported(created);
    } catch (err) {
      showToast(err.message || 'Failed to import template', 'error');
    } finally {
      setImporting(null);
    }
  };

  const rootEditorItems = editorItems.filter((i) => !i.parentTempId).sort((a, b) => (a.order || 0) - (b.order || 0));

  if (loading) {
    return (
      <div className="mobile-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="mobile-loading-spinner" />
        <p>Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="mobile-page">
      <div className="mobile-backlog-header">
        <button className="mobile-btn-icon" onClick={onBack}>
          <ChevronLeftIcon size={20} />
        </button>
        <h2><TemplateIcon size={18} /> Templates</h2>
        {isAdmin && (
          <button className="mobile-btn-icon" onClick={() => openEditor(null)}>
            <PlusIcon size={20} />
          </button>
        )}
      </div>

      <div className="mobile-backlog-body">
        {templates.length === 0 ? (
          <div className="mobile-backlog-empty">
            <TemplateIcon size={40} />
            <p>No templates yet.</p>
          </div>
        ) : (
          templates
            .filter((t) => !projectType || (t.templateType || 'scrum') === projectType)
            .map((template) => {
            const tType = template.templateType || 'scrum';
            return (
              <div key={template.id} className="mobile-template-card">
                <div className="mobile-template-card-info" onClick={() => { if (isAdmin) openEditor(template); }}>
                  <div style={{ fontWeight: 600, color: 'var(--white)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {template.name}
                    <span className={`template-type-badge template-type-badge--${tType}`}>
                      {tType === 'scrum' ? 'Scrum' : 'Kanban'}
                    </span>
                  </div>
                  {template.description && <div style={{ color: 'var(--gray-400)', fontSize: '0.8125rem' }}>{template.description}</div>}
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>{template.items?.length || 0} items</div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button className="mobile-btn mobile-btn-secondary" style={{ padding: '6px 10px', fontSize: '0.8125rem' }} onClick={() => handleImport(template)} disabled={importing === template.id}>
                    <DownloadIcon size={14} /> {importing === template.id ? '...' : 'Import'}
                  </button>
                  {isAdmin && (
                    <button className="mobile-btn-icon" onClick={() => setDeleteTarget(template)}>
                      <DeleteIcon size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showEditor && (
        <MobileSheet title={editingTemplate?.id ? 'Edit Template' : 'New Template'} onClose={() => setShowEditor(false)}>
          <div className="mobile-form">
            <div className="mobile-input-group">
              <label className="mobile-label">Name</label>
              <input className="mobile-input" value={editorName} onChange={(e) => setEditorName(e.target.value)} placeholder="Template name" />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-label">Description</label>
              <textarea className="mobile-input" rows={2} value={editorDesc} onChange={(e) => setEditorDesc(e.target.value)} placeholder="Optional" />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-label">Items</label>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                <button className="mobile-btn mobile-btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 8px' }} onClick={() => handleAddRoot('epic')}>+ Epic</button>
                <button className="mobile-btn mobile-btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 8px' }} onClick={() => handleAddRoot('story')}>+ Story</button>
                <button className="mobile-btn mobile-btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 8px' }} onClick={() => handleAddRoot('task')}>+ Task</button>
              </div>
              {rootEditorItems.length === 0 ? (
                <div style={{ color: 'var(--gray-500)', fontSize: '0.8125rem', padding: '12px 0' }}>No items yet.</div>
              ) : (
                rootEditorItems.map((item) => (
                  <MobileTemplateNode
                    key={item.tempId}
                    item={item}
                    allItems={editorItems}
                    depth={0}
                    onUpdate={handleUpdateItem}
                    onDelete={handleDeleteItem}
                    onAddChild={handleAddChild}
                  />
                ))
              )}
            </div>
            <button className="mobile-btn mobile-btn-primary" onClick={handleSaveEditor} disabled={editorSaving || !editorName.trim()}>
              {editorSaving ? 'Saving...' : (editingTemplate?.id ? 'Update' : 'Create')}
            </button>
          </div>
        </MobileSheet>
      )}

      {deleteTarget && (
        <ConfirmModal
          message={`Delete template "${deleteTarget.name}"?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
